// src/pages/AdminOrganizations.tsx - GÜNCELLENMIŞ (Kullanıcı listesi butonu eklenmiş)
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Building2, Plus, Copy, Pencil, Trash2, Users, X } from 'lucide-react';
import { apiService, Organization } from '../services/api';
import toast from 'react-hot-toast';
import AdminOrganizationUsers from './AdminOrganizationUsers';

export default function AdminOrganizations() {
  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    customCode: ''
  });

  // YENİ: Kullanıcı listesi görünümü için state
  const [showUsersList, setShowUsersList] = useState(false);
  const [selectedOrgForUsers, setSelectedOrgForUsers] = useState<Organization | null>(null);

  useEffect(() => {
    loadOrganizations();
  }, []);

  const loadOrganizations = async () => {
    try {
      setLoading(true);
      const data = await apiService.getOrganizations();
      setOrganizations(data);
    } catch (error: any) {
      toast.error('Organizasyonlar yüklenemedi');
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.name.trim()) {
      toast.error('Organizasyon adı gereklidir');
      return;
    }

    try {
      await apiService.createOrganization({
        name: formData.name,
        description: formData.description || undefined,
        customCode: formData.customCode || undefined
      });
      toast.success('Organizasyon oluşturuldu');
      setShowCreateModal(false);
      resetForm();
      loadOrganizations();
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Organizasyon oluşturulamadı';
      toast.error(message);
    }
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!selectedOrg) return;

    try {
      await apiService.updateOrganization(selectedOrg.id, {
        name: formData.name,
        description: formData.description || undefined,
        isActive: selectedOrg.isActive
      });
      toast.success('Organizasyon güncellendi');
      setShowEditModal(false);
      setSelectedOrg(null);
      resetForm();
      loadOrganizations();
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Organizasyon güncellenemedi';
      toast.error(message);
    }
  };

  const handleDelete = async (org: Organization) => {
    if (!window.confirm(`"${org.name}" organizasyonunu silmek istediğinizden emin misiniz?`)) {
      return;
    }

    try {
      await apiService.deleteOrganization(org.id);
      toast.success('Organizasyon silindi');
      loadOrganizations();
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Organizasyon silinemedi';
      toast.error(message);
    }
  };

  const copyCode = (code: string) => {
    navigator.clipboard.writeText(code);
    toast.success(`Kod kopyalandı: ${code}`);
  };

  const openEditModal = (org: Organization) => {
    setSelectedOrg(org);
    setFormData({
      name: org.name,
      description: org.description || '',
      customCode: ''
    });
    setShowEditModal(true);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      description: '',
      customCode: ''
    });
  };

  const closeModals = () => {
    setShowCreateModal(false);
    setShowEditModal(false);
    setSelectedOrg(null);
    resetForm();
  };

  // YENİ: Kullanıcı listesini göster
  const showUsersForOrg = (org: Organization) => {
    setSelectedOrgForUsers(org);
    setShowUsersList(true);
  };

  // YENİ: Kullanıcı listesinden geri dön
  const handleBackFromUsers = () => {
    setShowUsersList(false);
    setSelectedOrgForUsers(null);
  };

  // YENİ: Eğer kullanıcı listesi gösteriliyorsa, onu render et
  if (showUsersList && selectedOrgForUsers) {
    return (
      <AdminOrganizationUsers
        organizationId={selectedOrgForUsers.id}
        organizationName={selectedOrgForUsers.name}
        organizationCode={selectedOrgForUsers.code}
        onBack={handleBackFromUsers}
      />
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900 flex items-center gap-3">
              <Building2 className="w-8 h-8 text-blue-600" />
              Organizasyon Yönetimi
            </h1>
            <p className="text-gray-600 mt-2">
              Şirketler ve kuruluşlar için referans kodları oluşturun ve yönetin
            </p>
          </div>
          <Button
            onClick={() => setShowCreateModal(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white"
          >
            <Plus className="w-5 h-5 mr-2" />
            Yeni Organizasyon
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Toplam Organizasyon</p>
                  <p className="text-3xl font-bold text-gray-900">{organizations.length}</p>
                </div>
                <Building2 className="w-12 h-12 text-blue-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Aktif Organizasyon</p>
                  <p className="text-3xl font-bold text-green-600">
                    {organizations.filter(o => o.isActive).length}
                  </p>
                </div>
                <Building2 className="w-12 h-12 text-green-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">Toplam Kullanıcı</p>
                  <p className="text-3xl font-bold text-purple-600">
                    {organizations.reduce((sum, org) => sum + org.userCount, 0)}
                  </p>
                </div>
                <Users className="w-12 h-12 text-purple-600 opacity-20" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Organizations List */}
        <div className="grid gap-6">
          {organizations.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Building2 className="w-16 h-16 mx-auto text-gray-400 mb-4" />
                <p className="text-gray-600 text-lg">Henüz organizasyon bulunmuyor</p>
                <p className="text-gray-500 text-sm mt-2">
                  Yeni organizasyon eklemek için yukarıdaki butona tıklayın
                </p>
              </CardContent>
            </Card>
          ) : (
            organizations.map((org) => (
              <Card key={org.id} className="hover:shadow-lg transition-shadow">
                <CardContent className="pt-6">
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h3 className="text-2xl font-semibold text-gray-900">{org.name}</h3>
                        {org.isActive ? (
                          <span className="px-3 py-1 text-xs font-medium bg-green-100 text-green-700 rounded-full">
                            Aktif
                          </span>
                        ) : (
                          <span className="px-3 py-1 text-xs font-medium bg-gray-100 text-gray-700 rounded-full">
                            Pasif
                          </span>
                        )}
                      </div>
                      {org.description && (
                        <p className="text-gray-600 mb-4">{org.description}</p>
                      )}
                      <div className="flex items-center gap-6 text-sm text-gray-500">
                        <div className="flex items-center gap-2">
                          <Users className="w-4 h-4" />
                          <span>{org.userCount} kullanıcı</span>
                        </div>
                        <div>
                          Oluşturulma: {new Date(org.createdAt).toLocaleDateString('tr-TR')}
                        </div>
                      </div>
                    </div>

                    <div className="flex flex-col items-end gap-3">
                      {/* Kod gösterimi */}
                      <div className="bg-gradient-to-r from-blue-50 to-purple-50 px-6 py-3 rounded-lg border-2 border-blue-200">
                        <p className="text-xs text-gray-600 mb-1">Referans Kodu</p>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-2xl font-bold text-blue-600">
                            {org.code}
                          </span>
                          <button
                            onClick={() => copyCode(org.code)}
                            className="p-2 hover:bg-blue-100 rounded-lg transition-colors"
                            title="Kodu Kopyala"
                          >
                            <Copy className="w-4 h-4 text-blue-600" />
                          </button>
                        </div>
                      </div>

                      {/* Action buttons */}
                      <div className="flex gap-2">
                        {/* YENİ: Kullanıcıları Görüntüle Butonu */}
                        <button
                          onClick={() => showUsersForOrg(org)}
                          className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors"
                          title="Kullanıcıları Görüntüle"
                        >
                          <Users className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => openEditModal(org)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Düzenle"
                        >
                          <Pencil className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() => handleDelete(org)}
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Sil"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Yeni Organizasyon</CardTitle>
                <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CardDescription>
                Şirket veya kuruluş için referans kodu oluşturun
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleCreate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Organizasyon Adı <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Acme Corporation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Organizasyon hakkında kısa açıklama"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Özel Kod (Opsiyonel)
                  </label>
                  <Input
                    type="text"
                    value={formData.customCode}
                    onChange={(e) =>
                      setFormData({ ...formData, customCode: e.target.value.toUpperCase() })
                    }
                    placeholder="Boş bırakılırsa otomatik oluşturulur"
                    className="font-mono"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Örn: ACME2024, FIRMA123
                  </p>
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    onClick={closeModals}
                    variant="outline"
                    className="flex-1"
                  >
                    İptal
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Oluştur
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Edit Modal */}
      {showEditModal && selectedOrg && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <Card className="w-full max-w-md">
            <CardHeader>
              <div className="flex justify-between items-center">
                <CardTitle>Organizasyonu Düzenle</CardTitle>
                <button onClick={closeModals} className="p-1 hover:bg-gray-100 rounded">
                  <X className="w-5 h-5" />
                </button>
              </div>
              <CardDescription>
                Kod: <span className="font-mono font-bold">{selectedOrg.code}</span>
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleUpdate} className="space-y-4">
                <div>
                  <label className="block text-sm font-medium mb-2">
                    Organizasyon Adı <span className="text-red-500">*</span>
                  </label>
                  <Input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Örn: Acme Corporation"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium mb-2">
                    Açıklama
                  </label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                    rows={3}
                    placeholder="Organizasyon hakkında kısa açıklama"
                  />
                </div>

                <div className="flex gap-2 pt-4">
                  <Button
                    type="button"
                    onClick={closeModals}
                    variant="outline"
                    className="flex-1"
                  >
                    İptal
                  </Button>
                  <Button type="submit" className="flex-1 bg-blue-600 hover:bg-blue-700">
                    Güncelle
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}